// src/pages/Settings/MainSettings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const MainSettings = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [settings, setSettings] = useState({
    // Text Settings
    homescreenText: 'This application is for amusement purpose only',
    marqueeText: 'Welcome to Royal King!',
    maintenanceMessage: 'Site is under maintenance. Please check back later.',
    
    // Status Settings
    maintenanceStatus: 'Disable',
    globalBettingStatus: 'Enable',
    
    // Deposit Settings
    minDeposit: 10,
    maxDeposit: 20000,
    
    // Withdraw Settings
    minWithdraw: 10,
    maxWithdraw: 20000,
    
    // Auto Withdraw Settings
    autoWithdrawStatus: 'Disable',
    autoWithdrawPG: 'Dollar Bazar',
    minAutoWithdraw: 10,
    maxAutoWithdraw: 10,
    
    // Time Settings
    withdrawOpenTime: '09:00',
    withdrawCloseTime: '18:00',
    
    // Transfer Settings
    minTransfer: 50,
    maxTransfer: 10000,
    
    // Bonus Settings
    withdrawLimit: 4,
    welcomeBonus: 1,
    
    // Referral Settings
    referReward: 'Enable',
    referSelfReward: 8,
    referNewUserReward: 10,
    userReferBonus: 2,
    agentReferBonus: 10,
    
    // Bet Settings
    minSingleBet: 1,
    maxSingleBet: 200,
    
    // Game Settings
    starlineEnable: 'Enable',
    delhiEnable: 'Enable',
    
    // Links
    shareLink: 'https://royalking2.com',
    withdrawVideoLink: ''
  });

  // Fetch settings from API
  const fetchSettings = useCallback(async () => {
    try {
      setInitialLoading(true);
      const response = await api.get('/admin/settings');
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate numeric fields
    if (settings.minDeposit >= settings.maxDeposit) {
      toast.error('Min Deposit must be less than Max Deposit');
      setLoading(false);
      return;
    }
    
    if (settings.minWithdraw >= settings.maxWithdraw) {
      toast.error('Min Withdraw must be less than Max Withdraw');
      setLoading(false);
      return;
    }
    
    if (settings.minTransfer >= settings.maxTransfer) {
      toast.error('Min Transfer must be less than Max Transfer');
      setLoading(false);
      return;
    }
    
    if (settings.minSingleBet >= settings.maxSingleBet) {
      toast.error('Min Single Bet must be less than Max Single Bet');
      setLoading(false);
      return;
    }
    
    if (settings.minAutoWithdraw >= settings.maxAutoWithdraw) {
      toast.error('Min Auto Withdraw must be less than Max Auto Withdraw');
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.put('/admin/settings', settings);
      toast.success(response.data.message || 'Settings saved successfully!');
      
      // Refresh settings to ensure consistency
      await fetchSettings();
      
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default?')) {
      setSettings({
        homescreenText: 'This application is for amusement purpose only',
        marqueeText: 'Welcome to Royal King!',
        maintenanceMessage: 'Site is under maintenance. Please check back later.',
        maintenanceStatus: 'Disable',
        globalBettingStatus: 'Enable',
        minDeposit: 10,
        maxDeposit: 20000,
        minWithdraw: 10,
        maxWithdraw: 20000,
        autoWithdrawStatus: 'Disable',
        autoWithdrawPG: 'Dollar Bazar',
        minAutoWithdraw: 10,
        maxAutoWithdraw: 10,
        withdrawOpenTime: '09:00',
        withdrawCloseTime: '18:00',
        minTransfer: 50,
        maxTransfer: 10000,
        withdrawLimit: 4,
        welcomeBonus: 1,
        referReward: 'Enable',
        referSelfReward: 8,
        referNewUserReward: 10,
        userReferBonus: 2,
        agentReferBonus: 10,
        minSingleBet: 1,
        maxSingleBet: 200,
        starlineEnable: 'Enable',
        delhiEnable: 'Enable',
        shareLink: 'https://royalking2.com',
        withdrawVideoLink: ''
      });
      toast.info('Settings reset to default. Click Save to apply changes.');
    }
  };

  if (initialLoading) {
    return (
      <div className="bet-history-page">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bet-history-page">
      <div className="container-fluid">
        <div className="page-hedding-header d-flex justify-content-between align-items-center">
          <h1 className="page-heading h3 text-uppercase fs-4">Main Setting</h1>
          <button 
            type="button" 
            onClick={handleReset}
            className="btn btn-outline-warning"
            style={{ padding: '8px 16px', fontSize: '14px' }}
          >
            Reset to Default
          </button>
        </div>
        
        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Main Setting</h2>
          </div>

          <div className="admin-card-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {/* Homescreen Text */}
                <div className="col-12">
                  <label className="admin-label-one">Homescreen Text</label>
                  <input
                    type="text"
                    name="homescreenText"
                    value={settings.homescreenText}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    placeholder="This application is for amusement purpose only"
                  />
                </div>

                <div className="col-12">
                  <label className="admin-label-one">Marquee Text</label>
                  <input
                    type="text"
                    name="marqueeText"
                    value={settings.marqueeText}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    placeholder="Welcome text..."
                  />
                </div>

                <div className="col-12">
                  <label className="admin-label-one">App Maintenance Message</label>
                  <input
                    type="text"
                    name="maintenanceMessage"
                    value={settings.maintenanceMessage}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    placeholder="Maintenance message"
                  />
                </div>

                {/* Status Settings */}
                <div className="col-md-6">
                  <label className="admin-label-one">Maintenance Status</label>
                  <select
                    name="maintenanceStatus"
                    value={settings.maintenanceStatus}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                  >
                    <option value="Disable">Disable</option>
                    <option value="Enable">Enable</option>
                  </select>
                  <small className="text-muted">
                    {settings.maintenanceStatus === 'Enable' 
                      ? '⚠️ Maintenance mode is ON. Users will see maintenance message.' 
                      : '✅ Maintenance mode is OFF. Site is accessible.'}
                  </small>
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">Global Betting Status</label>
                  <select
                    name="globalBettingStatus"
                    value={settings.globalBettingStatus}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                  >
                    <option value="Enable">Enable</option>
                    <option value="Disable">Disable</option>
                  </select>
                  <small className="text-muted">
                    {settings.globalBettingStatus === 'Enable' 
                      ? 'Betting is allowed globally.' 
                      : 'Betting is disabled globally.'}
                  </small>
                </div>

                {/* Deposit Settings */}
                <div className="col-md-6">
                  <label className="admin-label-one">Min Deposit (₹)</label>
                  <input
                    type="number"
                    name="minDeposit"
                    value={settings.minDeposit}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">Max Deposit (₹)</label>
                  <input
                    type="number"
                    name="maxDeposit"
                    value={settings.maxDeposit}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                {/* Withdraw Settings */}
                <div className="col-md-6">
                  <label className="admin-label-one">Min Withdraw (₹)</label>
                  <input
                    type="number"
                    name="minWithdraw"
                    value={settings.minWithdraw}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">Max Withdraw (₹)</label>
                  <input
                    type="number"
                    name="maxWithdraw"
                    value={settings.maxWithdraw}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

             

                {/* Time Settings */}
                <div className="col-md-6">
                  <label className="admin-label-one">Withdraw Open Time</label>
                  <input
                    type="time"
                    name="withdrawOpenTime"
                    value={settings.withdrawOpenTime}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                  />
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">Withdraw Close Time</label>
                  <input
                    type="time"
                    name="withdrawCloseTime"
                    value={settings.withdrawCloseTime}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                  />
                </div>

                {/* Transfer Settings */}
                <div className="col-md-6">
                  <label className="admin-label-one">Min Transfer (₹)</label>
                  <input
                    type="number"
                    name="minTransfer"
                    value={settings.minTransfer}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">Max Transfer (₹)</label>
                  <input
                    type="number"
                    name="maxTransfer"
                    value={settings.maxTransfer}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                {/* Bonus Settings */}
                <div className="col-md-6">
                  <label className="admin-label-one">Withdraw Limit (per day)</label>
                  <input
                    type="number"
                    name="withdrawLimit"
                    value={settings.withdrawLimit}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">Welcome Bonus (%)</label>
                  <input
                    type="number"
                    name="welcomeBonus"
                    value={settings.welcomeBonus}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    max="100"
                    step="0.5"
                  />
                </div>

                {/* Referral Settings */}
                <div className="col-12">
                  <label className="admin-label-one">Refer Reward</label>
                  <select
                    name="referReward"
                    value={settings.referReward}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                  >
                    <option value="Enable">Enable</option>
                    <option value="Disable">Disable</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="admin-label-one">Refer Self Reward (₹)</label>
                  <input
                    type="number"
                    name="referSelfReward"
                    value={settings.referSelfReward}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                <div className="col-md-4">
                  <label className="admin-label-one">Refer New User Reward (₹)</label>
                  <input
                    type="number"
                    name="referNewUserReward"
                    value={settings.referNewUserReward}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                <div className="col-md-4">
                  <label className="admin-label-one">User Refer Bonus (₹)</label>
                  <input
                    type="number"
                    name="userReferBonus"
                    value={settings.userReferBonus}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">Agent Refer Bonus (₹)</label>
                  <input
                    type="number"
                    name="agentReferBonus"
                    value={settings.agentReferBonus}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                {/* Bet Settings */}
                <div className="col-md-6">
                  <label className="admin-label-one"> Min Single Bet (₹)</label>
                  <input
                    type="number"
                    name="minSingleBet"
                    value={settings.minSingleBet}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">Max Single Bet (₹)</label>
                  <input
                    type="number"
                    name="maxSingleBet"
                    value={settings.maxSingleBet}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    min="0"
                    step="1"
                  />
                </div>

                {/* Game Settings */}
             

                {/* Links */}
                <div className="col-12">
                  <label className="admin-label-one">Share Link</label>
                  <input
                    type="text"
                    name="shareLink"
                    value={settings.shareLink}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                  />
                </div>

                <div className="col-12">
                  <label className="admin-label-one">Withdraw Video Link</label>
                  <input
                    type="text"
                    name="withdrawVideoLink"
                    value={settings.withdrawVideoLink}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    placeholder="Video link"
                  />
                </div>

                {/* Submit Button */}
                <div className="col-12">
                  <button 
                    type="submit" 
                    className="admin-submit-one w-100" 
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainSettings;